import { CARD_HEIGHT, CARD_WIDTH } from '#app/og/dimensions.ts'
import { useMatches, useRouteLoaderData } from 'react-router'
import { useCanonical, useOrigin } from '#app/seo/hooks.ts'
import { Seo, type SeoProps } from '#app/seo/seo.tsx'

export type SeoHandle = (args: {
	loaderData: unknown
	params: Record<string, string>
	pathname: string
	origin: string
	canonical: string
	generalSettings?: {
		websiteName?: string | null
		twitterSite?: string | null
		twitterCreator?: string | null
		defaultOgImage?: string | null
	}
}) => SeoProps | null | undefined

export function SeoHead() {
	const matches = useMatches()
	const origin = useOrigin()
	const canonical = useCanonical()
	const rootData = useRouteLoaderData('root') as
		| {
				generalSettings?: {
					websiteName?: string | null
					twitterSite?: string | null
					twitterCreator?: string | null
					defaultOgImage?: string | null
				}
		  }
		| undefined

	let descriptor: SeoProps | null = null
	for (const m of matches) {
		const handle = m.handle as { seo?: SeoHandle } | undefined
		if (typeof handle?.seo === 'function') {
			const next = handle.seo({
				loaderData: m.data,
				params: m.params as Record<string, string>,
				pathname: m.pathname,
				origin,
				canonical,
				generalSettings: rootData?.generalSettings,
			})
			if (next)
				descriptor = mergeWithDefaults(
					next,
					rootData?.generalSettings,
					canonical,
				)
		}
	}

	/*
	 * What a page without its own `handle.seo` says about the site.
	 *
	 * It said "Local Dallas-Fort Worth Business Directory - DFWProfessionals.com"
	 * with "Find local professionals and businesses". Three problems at once:
	 * it carried the pre-rename brand as a bare domain, it described the site
	 * as a directory to browse rather than a publication that scores, and it
	 * spent the most valuable characters in the SERP on a hostname nobody
	 * searches. The rename sweep missed it because the string is the domain
	 * run together, not the brand with a space in it.
	 *
	 * Says what the site does and nothing it cannot defend. No "trusted", no
	 * "vetted", no promise of guidance.
	 */
	const fallbackTitle =
		'DFW Professionals | Dallas-Fort Worth businesses, scored on their reviews'
	const fallbackDescription =
		'Businesses across Dallas-Fort Worth, ranked on what reviewers wrote rather than the star they clicked. Published rubric, cited claims, no paid placement.'

	const fallback = mergeWithDefaults(
		{
			title: fallbackTitle,
			description: fallbackDescription,
			canonical,
			openGraph: {
				title: fallbackTitle,
				description: fallbackDescription,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: fallbackTitle,
				description: fallbackDescription,
			},
		},
		rootData?.generalSettings,
		canonical,
	)

	return <Seo {...(descriptor ?? fallback)} />
}

/**
 * The card every page falls back to when it has nothing more specific.
 *
 * Replaces a stock skyline captioned "Your trusted directory for
 * professionals". Both of those words are claims the site does not make: we
 * read public reviews with software against a published rubric, and we do not
 * vet, inspect or verify anyone. Every share of every page led with them.
 *
 * Absolute because Open Graph consumers do not resolve relative URLs, and
 * hardcoded to the canonical host rather than the request origin so a preview
 * fetched from a preview deployment still points at the live asset.
 *
 * 1200x630 at ~60KB. The source art is 1731x909, which is the same 1.905
 * ratio, so it downscales to the OG frame with no crop and the quiet left
 * two-thirds that overlaid text needs survives intact.
 */
const DEFAULT_OG_IMAGE = 'https://dfwprofessionals.com/img/og-default.jpg'

/**
 * Imported from the renderer rather than restated, so the numbers in the meta
 * tags cannot drift from the frame the cards are actually drawn at. A card
 * that says 1200x630 and is not gets letterboxed or cropped by every consumer
 * that trusts the tag.
 */
const OG_IMAGE_WIDTH = CARD_WIDTH
const OG_IMAGE_HEIGHT = CARD_HEIGHT

function mergeWithDefaults(
	input: SeoProps,
	generalSettings:
		| {
				websiteName?: string | null
				twitterSite?: string | null
				twitterCreator?: string | null
				defaultOgImage?: string | null
		  }
		| undefined,
	canonical: string,
): SeoProps {
	const siteName = generalSettings?.websiteName || 'DFW Professionals'
	const og = input.openGraph || {}
	const tw = input.twitter || {}

	/*
	 * Dimensions are defaulted here rather than per route because every image
	 * this site emits is the same frame: the generated cards render at exactly
	 * 1200x630, and the static fallback was cut to that size from art with the
	 * same 1.905 ratio. A route that ever points at something differently
	 * shaped must override both, which is why they are declared together.
	 *
	 * A route can still override any of this, since `...og` spreads last.
	 */
	const image = og.image || generalSettings?.defaultOgImage || DEFAULT_OG_IMAGE
	const twitterImage =
		tw.image || generalSettings?.defaultOgImage || DEFAULT_OG_IMAGE

	/*
	 * Describes the card, not the page. Alt text is read aloud by screen
	 * readers on social clients in place of an image nobody can see, so
	 * repeating the headline that already sits beside it adds nothing.
	 */
	const imageAlt = `${siteName}: ${input.title}`

	return {
		...input,
		canonical: input.canonical || canonical,
		openGraph: {
			siteName,
			image,
			imageWidth: OG_IMAGE_WIDTH,
			imageHeight: OG_IMAGE_HEIGHT,
			imageAlt,
			locale: 'en_US',
			url: og.url || input.canonical || canonical,
			...og,
		},
		twitter: {
			site: tw.site || generalSettings?.twitterSite || undefined,
			creator: tw.creator || generalSettings?.twitterCreator || undefined,
			image: twitterImage,
			imageAlt,
			card: tw.card || 'summary_large_image',
			...tw,
		},
	}
}
